import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import bodyMeasurementsImage from '../assets/Body Measurements.png';

import ProfileIcon from '../assets/Profile & Stats Default.png';
import WorkoutHistoryIcon from '../assets/Workout History Default.png';
import LineGraphIcon from '../assets/Line Graph Default.png';
import RulerIcon from '../assets/Ruler Default.png';
import BlogDefaultIcon from '../assets/Blog Default.png';


const formatDate = (date) => date.toISOString().split('T')[0];
const formatDisplayDate = (date) => date.toLocaleDateString('en-US', { 
  year: 'numeric', 
  month: 'short', 
  day: 'numeric' 
});

export default function Profile({ user }) {
  const [profile, setProfile] = useState({
    name: '',
    gender: '',
    date_of_birth: '',
    height: '',
    target_weight: '',
    activity_level: 'moderate',
    fitness_goal: 'maintain',
    injuries_notes: ''
  });
  
  const [measurements, setMeasurements] = useState([]);
  const [measurementForm, setMeasurementForm] = useState({
    weight: '',
    body_fat_percentage: '',
    muscle_percentage: '',
    chest: '',
    waist: '',
    hips: '',
    bicep: '',
    thigh: '',
    neck: '',
    notes: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('weight');
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showMeasurementHelp, setShowMeasurementHelp] = useState(false);

  // Fetch user profile
  const fetchProfile = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error fetching profile:', error);
        setError('Failed to load profile');
      } else if (data) {
        setProfile(data);
      } else {
        // No profile exists yet, show the form
        setEditingProfile(true);
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
      setError('Failed to load profile');
    }
  }, [user.id]);

  // Fetch measurements
  const fetchMeasurements = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('body_measurements')
        .select('*')
        .eq('user_id', user.id)
        .order('measurement_date', { ascending: true });

      if (error) {
        console.error('Error fetching measurements:', error);
        setError('Failed to load measurements');
      } else if (data) {
        setMeasurements(data);
      }
    } catch (err) {
      console.error('Measurements fetch error:', err);
      setError('Failed to load measurements');
    }
  }, [user.id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProfile(), fetchMeasurements()]);
      setLoading(false);
    };
    
    if (user?.id) {
      loadData();
    }
  }, [user?.id, fetchProfile, fetchMeasurements]);

  // Save profile
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const profileData = {
        ...profile,
        user_id: user.id,
        height: profile.height ? parseFloat(profile.height) : null,
        target_weight: profile.target_weight ? parseFloat(profile.target_weight) : null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('user_profiles')
        .upsert(profileData, { onConflict: ['user_id'] });

      if (error) throw error;
      
      // Refresh profile data
      await fetchProfile();
      setEditingProfile(false);
      
      // Dispatch event to update name in App.js header
      window.dispatchEvent(new CustomEvent('profile-updated'));
    } catch (err) {
      console.error('Profile save error:', err);
      setError('Failed to save profile');
    }
  };

  // Add measurement
  const handleMeasurementSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if at least one measurement is provided
    const hasAnyMeasurement = Object.entries(measurementForm).some(([key, value]) => 
      key !== 'notes' && value && value.trim() !== ''
    );

    if (!hasAnyMeasurement) {
      setError('Please enter at least one measurement');
      return;
    }

    try {
      const measurementData = {
        user_id: user.id,
        measurement_date: formatDate(new Date()),
        weight: measurementForm.weight ? parseFloat(measurementForm.weight) : null,
        body_fat_percentage: measurementForm.body_fat_percentage ? parseFloat(measurementForm.body_fat_percentage) : null,
        muscle_percentage: measurementForm.muscle_percentage ? parseFloat(measurementForm.muscle_percentage) : null,
        chest: measurementForm.chest ? parseFloat(measurementForm.chest) : null,
        waist: measurementForm.waist ? parseFloat(measurementForm.waist) : null,
        hips: measurementForm.hips ? parseFloat(measurementForm.hips) : null,
        bicep: measurementForm.bicep ? parseFloat(measurementForm.bicep) : null,
        thigh: measurementForm.thigh ? parseFloat(measurementForm.thigh) : null,
        neck: measurementForm.neck ? parseFloat(measurementForm.neck) : null,
        notes: measurementForm.notes?.trim() || ''
      };

      if (editingMeasurement) {
        const { error } = await supabase
          .from('body_measurements')
          .update(measurementData)
          .eq('id', editingMeasurement.id);

        if (error) throw error;
        setEditingMeasurement(null);
      } else {
        const { error } = await supabase
          .from('body_measurements')
          .insert([measurementData]);

        if (error) throw error;
      }

      // Reset form and refresh data
      setMeasurementForm({
        weight: '',
        body_fat_percentage: '',
        muscle_percentage: '',
        chest: '',
        waist: '',
        hips: '',
        bicep: '',
        thigh: '',
        neck: '',
        notes: ''
      });
      
      await fetchMeasurements();
    } catch (err) {
      console.error('Measurement save error:', err);
      setError('Failed to save measurement');
    }
  };

  // Edit measurement
  const handleEditMeasurement = (measurement) => {
    setEditingMeasurement(measurement);
    setMeasurementForm({
      weight: measurement.weight || '',
      body_fat_percentage: measurement.body_fat_percentage || '',
      muscle_percentage: measurement.muscle_percentage || '',
      chest: measurement.chest || '',
      waist: measurement.waist || '',
      hips: measurement.hips || '',
      bicep: measurement.bicep || '',
      thigh: measurement.thigh || '',
      neck: measurement.neck || '',
      notes: measurement.notes || ''
    });
  };

  // Delete measurement
  const handleDeleteMeasurement = async (measurement) => {
    if (!window.confirm('Delete this measurement record?')) return;

    try {
      const { error } = await supabase
        .from('body_measurements')
        .delete()
        .eq('id', measurement.id);

      if (error) throw error;
      await fetchMeasurements();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete measurement');
    }
  };

  // Calculate BMI
  const calculateBMI = (weight, height) => {
    if (!weight || !height) return null;
    const heightInM = height / 100; // convert cm to m
    return (weight / (heightInM * heightInM)).toFixed(1);
  };

  // Get BMI category
  const getBMICategory = (bmi) => {
    if (!bmi) return '';
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  };

  // Calculate age
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Prepare chart data
  const getChartData = (metric) => {
    return measurements
      .filter(m => m[metric] !== null && m[metric] !== undefined)
      .map(m => ({
        date: m.measurement_date,
        value: m[metric],
        displayDate: formatDisplayDate(new Date(m.measurement_date + 'T00:00:00'))
      }));
  };

  // Check if profile has basic information
  const hasProfileData = profile.gender || profile.date_of_birth || profile.height || profile.target_weight;

  const currentWeight = measurements.length > 0 ? measurements[measurements.length - 1].weight : null;
  const currentBMI = calculateBMI(currentWeight, profile.height);
  const age = calculateAge(profile.date_of_birth);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Profile Information - Only show if editing or no profile data exists */}
      {(editingProfile || !hasProfileData) && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <img src={ProfileIcon} alt="Profile" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              Profile Information
            </h2>
            {hasProfileData && (
              <button
                onClick={() => setEditingProfile(false)}
                className="text-gray-300 hover:text-gray-200 text-sm"
              >
                Cancel
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-6">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={profile.name}
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Gender</label>
                <select
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-gray-900 [&>option]:text-white"
                  value={profile.gender}
                  onChange={(e) => setProfile({...profile, gender: e.target.value})}
                >
                  <option value="" className="bg-gray-900 text-white">Select Gender</option>
                  <option value="male" className="bg-gray-900 text-white">Male</option>
                  <option value="female" className="bg-gray-900 text-white">Female</option>
                  <option value="other" className="bg-gray-900 text-white">Other</option>
                  <option value="prefer_not_to_say" className="bg-gray-900 text-white">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Date of Birth</label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={profile.date_of_birth}
                  onChange={(e) => setProfile({...profile, date_of_birth: e.target.value})}
                />
                {age && <p className="text-purple-300 text-sm mt-1">Age: {age} years</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Height (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={profile.height}
                  onChange={(e) => setProfile({...profile, height: e.target.value})}
                  placeholder="e.g., 175"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Target Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={profile.target_weight}
                  onChange={(e) => setProfile({...profile, target_weight: e.target.value})}
                  placeholder="e.g., 75"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Activity Level</label>
                <select
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-gray-900 [&>option]:text-white"
                  value={profile.activity_level}
                  onChange={(e) => setProfile({...profile, activity_level: e.target.value})}
                >
                  <option value="sedentary" className="bg-gray-900 text-white">Sedentary (little/no exercise)</option>
                  <option value="light" className="bg-gray-900 text-white">Lightly active (light exercise 1-3 days/week)</option>
                  <option value="moderate" className="bg-gray-900 text-white">Moderately active (moderate exercise 3-5 days/week)</option>
                  <option value="very" className="bg-gray-900 text-white">Very active (hard exercise 6-7 days/week)</option>
                  <option value="extremely" className="bg-gray-900 text-white">Extremely active (very hard exercise, physical job)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Fitness Goal</label>
                <select
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-gray-900 [&>option]:text-white"
                  value={profile.fitness_goal}
                  onChange={(e) => setProfile({...profile, fitness_goal: e.target.value})}
                >
                  <option value="lose_weight" className="bg-gray-900 text-white">Lose Weight</option>
                  <option value="gain_weight" className="bg-gray-900 text-white">Gain Weight</option>
                  <option value="maintain" className="bg-gray-900 text-white">Maintain Weight</option>
                  <option value="build_muscle" className="bg-gray-900 text-white">Build Muscle</option>
                  <option value="improve_endurance" className="bg-gray-900 text-white">Improve Endurance</option>
                  <option value="general_fitness" className="bg-gray-900 text-white">General Fitness</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Injuries/Medical Notes</label>
              <textarea
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows="3"
                value={profile.injuries_notes}
                onChange={(e) => setProfile({...profile, injuries_notes: e.target.value})}
                placeholder="Any injuries, medical conditions, or notes to consider..."
              />
            </div>

            <button
              type="submit"
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
            >
              {hasProfileData ? 'Update Profile' : 'Save Profile'}
            </button>
          </form>
        </div>
      )}

      {/* Profile Summary - Only show if profile exists and not editing */}
      {hasProfileData && !editingProfile && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <img src={ProfileIcon} alt="Profile" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              Profile Summary
            </h2>
            <button
              onClick={() => setEditingProfile(true)}
              className="bg-purple-500/80 hover:bg-purple-500 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm"
            >
              Edit Profile
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {profile.name && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Name</div>
                <div className="text-white font-medium">{profile.name}</div>
              </div>
            )}
            {profile.gender && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Gender</div>
                <div className="text-white font-medium capitalize">{profile.gender.replace('_', ' ')}</div>
              </div>
            )}
            {age && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Age</div>
                <div className="text-white font-medium">{age} years</div>
              </div>
            )}
            {profile.height && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Height</div>
                <div className="text-white font-medium">{profile.height} cm</div>
              </div>
            )}
            {profile.target_weight && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Target Weight</div>
                <div className="text-white font-medium">{profile.target_weight} kg</div>
              </div>
            )}
            {profile.activity_level && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Activity Level</div>
                <div className="text-white font-medium capitalize">{profile.activity_level}</div>
              </div>
            )}
            {profile.fitness_goal && (
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-purple-300">Fitness Goal</div>
                <div className="text-white font-medium capitalize">{profile.fitness_goal.replace('_', ' ')}</div>
              </div>
            )}
          </div>

          {profile.injuries_notes && (
            <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <div className="text-yellow-300 text-sm font-medium mb-1">⚠️ Medical Notes</div>
              <div className="text-yellow-200 text-sm">{profile.injuries_notes}</div>
            </div>
          )}
        </div>
      )}

      {/* Current Stats */}
      {(currentWeight || currentBMI) && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <img src={WorkoutHistoryIcon} alt="History" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
            Current Stats
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {currentWeight && (
              <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{currentWeight} kg</div>
                <div className="text-purple-200 text-sm">Current Weight</div>
              </div>
            )}
            {currentBMI && (
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{currentBMI}</div>
                <div className="text-blue-200 text-sm">BMI - {getBMICategory(currentBMI)}</div>
              </div>
            )}
            {profile.target_weight && currentWeight && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">
                  {(currentWeight - profile.target_weight > 0 ? '+' : '')}{(currentWeight - profile.target_weight).toFixed(1)} kg
                </div>
                <div className="text-green-200 text-sm">From Target</div>
              </div>
            )}
            {age && (
              <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-white">{age}</div>
                <div className="text-orange-200 text-sm">Years Old</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Measurements */}
      {measurements.length > 0 && (() => {
        const latestMeasurement = measurements[measurements.length - 1];
        const hasBodyMeasurements = latestMeasurement.chest || latestMeasurement.waist || 
                                     latestMeasurement.hips || latestMeasurement.bicep || 
                                     latestMeasurement.thigh || latestMeasurement.neck;
        
        return hasBodyMeasurements && (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <img src={RulerIcon} alt="Ruler" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              Current Measurements
              <span className="ml-auto text-sm font-normal text-purple-200">
                {formatDisplayDate(new Date(latestMeasurement.measurement_date + 'T00:00:00'))}
              </span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {latestMeasurement.neck && (
                <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.neck}</div>
                  <div className="text-purple-200 text-sm">Neck (cm)</div>
                </div>
              )}
              {latestMeasurement.chest && (
                <div className="bg-pink-500/20 border border-pink-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.chest}</div>
                  <div className="text-pink-200 text-sm">Chest (cm)</div>
                </div>
              )}
              {latestMeasurement.bicep && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.bicep}</div>
                  <div className="text-blue-200 text-sm">Bicep (cm)</div>
                </div>
              )}
              {latestMeasurement.waist && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.waist}</div>
                  <div className="text-green-200 text-sm">Waist (cm)</div>
                </div>
              )}
              {latestMeasurement.hips && (
                <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.hips}</div>
                  <div className="text-orange-200 text-sm">Hips (cm)</div>
                </div>
              )}
              {latestMeasurement.thigh && (
                <div className="bg-indigo-500/20 border border-indigo-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.thigh}</div>
                  <div className="text-indigo-200 text-sm">Thigh (cm)</div>
                </div>
              )}
            </div>
            {latestMeasurement.body_fat_percentage && latestMeasurement.muscle_percentage && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.body_fat_percentage}%</div>
                  <div className="text-red-200 text-sm">Body Fat</div>
                </div>
                <div className="bg-teal-500/20 border border-teal-500/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{latestMeasurement.muscle_percentage}%</div>
                  <div className="text-teal-200 text-sm">Muscle</div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Progress Charts */}
      {measurements.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <img src={LineGraphIcon} alt="Graph" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              Progress Tracking
            </h3>
            <select
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              <option value="weight">Weight</option>
              <option value="body_fat_percentage">Body Fat %</option>
              <option value="muscle_percentage">Muscle %</option>
              <option value="waist">Waist</option>
              <option value="chest">Chest</option>
              <option value="bicep">Bicep</option>
            </select>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={getChartData(selectedMetric)} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#666" />
              <XAxis dataKey="displayDate" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#8884d8" 
                activeDot={{ r: 8 }} 
                name={selectedMetric.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Add Measurements */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
          <img src={RulerIcon} alt="Ruler" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          {editingMeasurement ? 'Edit Measurement' : 'Add New Measurement'}
          <button
            onClick={() => setShowMeasurementHelp(true)}
            className="ml-2 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center text-white text-xs sm:text-sm transition-colors"
            title="Measurement Guide"
            type="button"
          >
            ?
          </button>
        </h3>

        <form onSubmit={handleMeasurementSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Weight (kg)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.weight}
                onChange={(e) => setMeasurementForm({...measurementForm, weight: e.target.value})}
                placeholder="75.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Body Fat %</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.body_fat_percentage}
                onChange={(e) => setMeasurementForm({...measurementForm, body_fat_percentage: e.target.value})}
                placeholder="15.2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Muscle %</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.muscle_percentage}
                onChange={(e) => setMeasurementForm({...measurementForm, muscle_percentage: e.target.value})}
                placeholder="35.8"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Chest (cm)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.chest}
                onChange={(e) => setMeasurementForm({...measurementForm, chest: e.target.value})}
                placeholder="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Waist (cm)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.waist}
                onChange={(e) => setMeasurementForm({...measurementForm, waist: e.target.value})}
                placeholder="85"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Hips (cm)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.hips}
                onChange={(e) => setMeasurementForm({...measurementForm, hips: e.target.value})}
                placeholder="95"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Bicep (cm)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.bicep}
                onChange={(e) => setMeasurementForm({...measurementForm, bicep: e.target.value})}
                placeholder="35"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Thigh (cm)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.thigh}
                onChange={(e) => setMeasurementForm({...measurementForm, thigh: e.target.value})}
                placeholder="55"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Neck (cm)</label>
              <input
                type="number"
                step="0.1"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={measurementForm.neck}
                onChange={(e) => setMeasurementForm({...measurementForm, neck: e.target.value})}
                placeholder="40"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">Notes</label>
            <input
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={measurementForm.notes}
              onChange={(e) => setMeasurementForm({...measurementForm, notes: e.target.value})}
              placeholder="Optional notes about this measurement..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
            >
              {editingMeasurement ? 'Update Measurement' : 'Add Measurement'}
            </button>

            {editingMeasurement && (
              <button
                type="button"
                onClick={() => {
                  setEditingMeasurement(null);
                  setMeasurementForm({
                    weight: '',
                    body_fat_percentage: '',
                    muscle_percentage: '',
                    chest: '',
                    waist: '',
                    hips: '',
                    bicep: '',
                    thigh: '',
                    neck: '',
                    notes: ''
                  });
                }}
                className="bg-gray-500/80 hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Measurement History */}
      {measurements.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <img src={BlogDefaultIcon} alt="Blog" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
            Measurement History
          </h3>

          <div className="space-y-4">
            {measurements.slice().reverse().map((measurement) => (
              <div key={measurement.id} className="bg-white/10 border border-white/10 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-semibold text-white">
                    {formatDisplayDate(new Date(measurement.measurement_date + 'T00:00:00'))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditMeasurement(measurement)}
                      className="text-blue-300 hover:text-blue-200 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteMeasurement(measurement)}
                      className="text-red-300 hover:text-red-200 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-sm text-purple-200">
                  {measurement.weight && <div>Weight: {measurement.weight} kg</div>}
                  {measurement.body_fat_percentage && <div>Body Fat: {measurement.body_fat_percentage}%</div>}
                  {measurement.muscle_percentage && <div>Muscle: {measurement.muscle_percentage}%</div>}
                  {measurement.chest && <div>Chest: {measurement.chest} cm</div>}
                  {measurement.waist && <div>Waist: {measurement.waist} cm</div>}
                  {measurement.hips && <div>Hips: {measurement.hips} cm</div>}
                  {measurement.bicep && <div>Bicep: {measurement.bicep} cm</div>}
                  {measurement.thigh && <div>Thigh: {measurement.thigh} cm</div>}
                  {measurement.neck && <div>Neck: {measurement.neck} cm</div>}
                </div>
                
                {measurement.notes && (
                  <div className="mt-2 text-sm text-purple-300 italic">
                    Note: {measurement.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Measurement Help Modal */}
      {showMeasurementHelp && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowMeasurementHelp(false)}>
          <div className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-md rounded-2xl border border-white/20 p-6 max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">How to Measure Your Body</h3>
              <button
                onClick={() => setShowMeasurementHelp(false)}
                className="text-white/60 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            {/* Body Measurements Image */}
            <div className="mb-6 bg-white/10 rounded-lg p-4 flex justify-center">
              <img 
                src={bodyMeasurementsImage} 
                alt="Body Measurements Guide" 
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '400px' }}
              />
            </div>

            <div className="space-y-5 text-white/90">
              <div>
                <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Neck
                </h4>
                <p className="leading-relaxed ml-4">
                  Measure around the base of your neck, just below the Adam's apple. Keep the tape measure horizontal and snug but not tight. This is typically the narrowest part of your neck.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Chest
                </h4>
                <p className="leading-relaxed ml-4">
                  Place the tape measure around the fullest part of your chest, typically at nipple level. Keep your arms relaxed at your sides and breathe normally. The tape should be parallel to the floor and fit snugly without compressing the skin.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Biceps
                </h4>
                <p className="leading-relaxed ml-4">
                  Measure around the fullest part of your upper arm (bicep) with your arm relaxed at your side. For flexed measurements, flex your bicep and measure at the peak of the muscle. Always measure the same arm and note whether it's relaxed or flexed.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Waist
                </h4>
                <p className="leading-relaxed ml-4">
                  Measure around your natural waistline, which is typically the narrowest part of your torso, usually just above your belly button. Keep the tape parallel to the floor, breathe normally, and don't suck in your stomach. The tape should be snug but not compressing.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Hips
                </h4>
                <p className="leading-relaxed ml-4">
                  Measure around the fullest part of your hips and buttocks. Stand with your feet together and keep the tape measure parallel to the floor. This measurement should be taken at the widest point around your hip area.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Thighs
                </h4>
                <p className="leading-relaxed ml-4">
                  Measure around the fullest part of your thigh, typically about 2-3 inches below your crotch. Stand with your feet slightly apart and weight evenly distributed. Always measure the same leg for consistency.
                </p>
              </div>

              <div className="mt-6 p-4 bg-purple-500/20 rounded-lg border border-purple-400/30">
                <h4 className="text-lg font-semibold text-white mb-2">Tips for Accurate Measurements</h4>
                <ul className="space-y-2 ml-4 text-sm">
                  <li className="flex items-start">
                    <span className="text-purple-300 mr-2">→</span>
                    <span>Take measurements at the same time of day (preferably in the morning) for consistency</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-300 mr-2">→</span>
                    <span>Use a flexible tape measure designed for body measurements</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-300 mr-2">→</span>
                    <span>Stand naturally and avoid tensing muscles unless specifically measuring flexed</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-300 mr-2">→</span>
                    <span>Take measurements directly on skin or over light clothing for accuracy</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-300 mr-2">→</span>
                    <span>Record measurements every 2-4 weeks to track progress effectively</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setShowMeasurementHelp(false)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}